// src/terms/TermsOfService.js

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

function AccordionItem({ item, isOpen, onToggle }) {
  return (
    <div
      className="
        overflow-hidden rounded-2xl
        border border-[rgb(var(--border))]
        bg-[rgb(var(--surface))]
        shadow-sm
      "
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="
          flex w-full items-start justify-between gap-4
          px-5 py-5 text-left
          transition-colors
          hover:bg-[rgb(var(--elevated))]
          focus:outline-none
          focus:ring-2
          focus:ring-inset
          focus:ring-[rgb(var(--brand)/0.45)]
        "
      >
        <div className="min-w-0">
          <div className="text-[15px] font-extrabold tracking-tight text-[rgb(var(--text-main))] sm:text-base">
            {item.q}
          </div>

          {item.short ? (
            <div className="mt-1.5 text-sm leading-6 text-[rgb(var(--text-muted))]">
              {item.short}
            </div>
          ) : null}
        </div>

        <span
          className="shrink-0 pt-0.5 text-xl font-semibold text-[rgb(var(--text-muted))]"
          aria-hidden="true"
        >
          {isOpen ? "−" : "+"}
        </span>
      </button>

      {isOpen ? (
        <div className="border-t border-[rgb(var(--border))] px-5 pb-5 pt-4">
          <div className="rounded-2xl bg-[rgb(var(--elevated))] px-4 py-4 sm:px-5">
            <div className="space-y-3 text-sm leading-7 text-[rgb(var(--text-main))]">
              {item.a.split("\n").map((line, i) => (
                <p key={i}>{line || "\u00A0"}</p>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function TermsOfService() {
  const LAST_UPDATED = "September 11, 2026";

  const items = useMemo(
    () => [
      {
        q: "1) Acceptance of these Terms",
        short: "These Terms govern your use of Veacon.",
        a: `These Terms of Service (“Terms”) govern access to and use of Veacon's mobile application, website, and related services (collectively, the “Service”).

By creating an account or using the Service, you agree to these Terms.

If you do not agree to these Terms, do not use the Service.`,
      },
      {
        q: "2) Eligibility and accounts",
        short: "Veacon is intended for users age 16 and older.",
        a: `Veacon is intended for users who are at least 16 years old.

You must be at least 16 years old to create or maintain a Veacon account.

You are responsible for providing accurate account information and for activity that occurs through your account.

Do not impersonate another person or create an account using information you are not authorized to use.

You are responsible for keeping your login credentials secure.

If you believe your account has been accessed without authorization or may no longer be secure, contact support@veacon.app and take appropriate steps to secure the account.`,
      },
      {
        q: "3) Privacy",
        short: "The Privacy Policy explains how Veacon handles information.",
        a: `Use of Veacon is also subject to the Veacon Privacy Policy.

The Privacy Policy explains how Veacon collects, uses, shares, retains, and protects information and describes certain choices and controls available to users.

The Privacy Policy is available through the Service and at veacon.app/privacy.`,
      },
      {
        q: "4) Activities, events, hosting, and safety",
        short: "Use judgment when hosting, joining, or attending activities and events.",
        a: `Veacon helps users discover, create, plan, host, join, and attend activities and events and interact with other users in connection with those activities and events.

You are responsible for deciding whether an activity, event, location, host, organizer, participant, or other interaction is appropriate for you.

Unless expressly stated otherwise, Veacon does not independently verify the identity, background, qualifications, safety, location, venue, organizer, accuracy, or conduct of every user, host, activity, or event.

Use reasonable judgment, follow applicable laws, respect venue and organizer requirements, and take appropriate precautions when hosting, attending, joining, or otherwise participating in activities or events.

You remain responsible for your judgment, decisions, conduct, participation, hosting, and interactions with others in connection with Veacon, activities, and events.`,
      },
      {
        q: "5) Acceptable use",
        short: "Do not harm others or abuse the Service.",
        a: `The Service may not be used to:
• harass, threaten, stalk, intimidate, exploit, or harm another person
• discriminate against another person in violation of applicable law
• impersonate another person or intentionally misrepresent your identity
• engage in or promote scams, fraud, deceptive conduct, or unlawful activity
• post or distribute illegal, abusive, threatening, fraudulent, or infringing content
• interfere with another user's ability to access or use the Service
• attempt to gain unauthorized access to accounts, systems, features, or data
• intentionally disrupt, overload, damage, compromise, or interfere with the Service or its security
• scrape, crawl, reverse engineer, or use automated means to access or interact with the Service except where expressly authorized by Veacon or permitted by applicable law
• use Veacon in violation of applicable laws or regulations

Reasonable action may be taken when conduct violates these Terms or creates a safety, security, legal, or operational risk for users or the Service.`,
      },
      {
        q: "6) User content",
        short: "You keep ownership of your content and remain responsible for it.",
        a: `You retain ownership of content you create or submit to Veacon, such as profile information, activity or event information, descriptions, messages, images, or other materials.

You are responsible for content you submit and for ensuring that you have the rights necessary to use and share it.

Content may not be uploaded or shared if it violates applicable law, infringes another person's rights, or violates these Terms.`,
      },
      {
        q: "7) License to operate the Service",
        short: "You give Veacon the limited rights needed to provide the Service.",
        a: `By submitting content to Veacon, you grant Veacon a limited, non-exclusive, worldwide license to host, store, reproduce, display, distribute, process, and otherwise use that content as reasonably necessary to operate, provide, maintain, secure, and improve the Service.

This license is subject to applicable privacy and visibility settings.

You continue to own your content.

This license does not give Veacon ownership of your content and is intended only to provide the rights reasonably necessary to operate the Service.`,
      },
      {
        q: "8) Reports, blocks, and moderation",
        short: "Reports may be reviewed and harmful behavior may be restricted.",
        a: `Veacon provides tools that may allow users to block other users or report safety concerns, abuse, inappropriate content, impersonation, scams, suspicious behavior, or other potential violations.

Reports may be reviewed or investigated, and reasonable action may be taken where appropriate.

Multiple, repeated, or related reports concerning an account, activity, event, hosting, or pattern of conduct may result in additional review or investigation of potentially suspicious, abusive, unsafe, or prohibited behavior.

Depending on the circumstances, actions may include:
• restricting interactions
• removing or limiting content
• limiting access to features
• suspending accounts
• terminating accounts
• preserving information where reasonably necessary for safety, security, enforcement, or legal purposes

A report does not necessarily establish that a violation occurred, and a particular action is not required in response to every report.

Submitting false, abusive, or intentionally misleading reports may itself violate these Terms.`,
      },
      {
        q: "9) Third-party events, information, links, and services",
        short: "Some event information and services originate from independent third parties.",
        a: `Veacon may display or make available event information, activity information, venue information, dates and times, pricing, ticket information, registration options, descriptions, images, links, or other materials originating from third-party organizers, venues, platforms, websites, or other sources.

Some listings or portions of listings available through Veacon may contain, incorporate, organize, reproduce, summarize, or otherwise present information originating from third-party sources. Veacon does not necessarily create or control the underlying third-party information.

Third-party information may be incomplete, inaccurate, unavailable, delayed, or become outdated as events, activities, schedules, prices, availability, venues, registration requirements, or other circumstances change.

Important information should be verified with the applicable organizer, venue, registration provider, or other source when appropriate before making decisions based on a listing.

Independent third parties operate separately from Veacon.

Unless expressly stated otherwise, Veacon does not control and is not responsible for:
• third-party websites or services
• ticket or registration transactions
• cancellations or schedule changes
• refunds
• venue operations
• organizer conduct
• third-party products or offerings
• the continued availability, completeness, or accuracy of third-party information

Interactions with independent third parties may be governed by their own terms, policies, requirements, and rules.`,
      },
      {
        q: "10) Veacon intellectual property",
        short: "Veacon software, branding, and proprietary platform materials are protected.",
        a: `Veacon's software, branding, logos, interfaces, designs, features, and proprietary platform materials are owned by or licensed for use in connection with Veacon and are protected by applicable intellectual-property laws.

Except where permitted by applicable law or expressly authorized by Veacon, Veacon's proprietary materials may not be copied, reproduced, distributed, sold, licensed, reverse engineered, or used to create derivative works.

These Terms do not transfer ownership of Veacon intellectual property to you.

Third-party content displayed through Veacon remains subject to any rights held by the applicable third party.`,
      },
      {
        q: "11) Hosting, plans, and user interactions",
        short: "Users remain responsible for the plans and activities they organize or join.",
        a: `Users may create, host, join, leave, update, or cancel plans, activities, events, sessions, or other social interactions through Veacon.

Hosts and participants are responsible for their own decisions and conduct, communicating appropriately, and complying with applicable laws and venue requirements.

Hosts are responsible for exercising appropriate judgment regarding activities or plans they create or host. Participants remain responsible for deciding whether to join, attend, remain at, or leave an activity, event, or plan.

A host may change or end a user-created activity or plan, and participants may choose to leave or stop participating.

Veacon is not a party to agreements or arrangements made directly between users unless expressly stated otherwise.`,
      },
      {
        q: "12) Account deletion and termination",
        short: "Accounts can be deleted; access may be restricted for serious violations or risks.",
        a: `You may permanently delete your Veacon account using the account deletion feature provided through the Service.

Account deletion is handled in accordance with the Veacon Privacy Policy and applicable retention requirements.

Access to Veacon may be restricted, suspended, or terminated where an account is reasonably believed to:
• violate these Terms or applicable policies
• create a safety or security risk
• engage in fraud, abuse, or unlawful conduct
• interfere with the Service or other users
• expose Veacon, the Service, or others to significant legal, safety, security, or operational risk

Where appropriate, action may be taken without prior notice.

Termination does not prevent provisions that by their nature should survive termination from continuing to apply.`,
      },
      {
        q: "13) Changes to the Service",
        short: "Features may be added, changed, restricted, or discontinued over time.",
        a: `Veacon is an evolving service.

Features or portions of the Service may be added, modified, restricted, suspended, replaced, or discontinued from time to time.

Additional terms or requirements may apply to certain future features where appropriate.

The continued availability of every feature or uninterrupted operation of the Service is not guaranteed.`,
      },
      {
        q: "14) Disclaimers",
        short: "The Service is provided without guarantees beyond those required by law.",
        a: `To the fullest extent permitted by applicable law, the Service is provided “as is” and “as available.”

Veacon does not guarantee that:
• the Service will always be available, uninterrupted, secure, or error-free
• information provided by users or third parties will always be accurate, current, or complete
• an activity, event, host, participant, organizer, or venue will meet your expectations
• interactions between users will be safe, successful, appropriate, or suitable for you
• an activity, event, plan, or hosting experience will occur as described or expected

To the fullest extent permitted by applicable law, warranties that may otherwise apply, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement, are disclaimed.

Nothing in these Terms excludes warranties, protections, or rights that cannot legally be excluded.`,
      },
      {
        q: "15) Limitation of liability",
        short: "Liability is limited to the extent permitted by law.",
        a: `To the fullest extent permitted by applicable law, Veacon will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages arising from or related to use of the Service.

This may include loss of data, profits, goodwill, opportunities, or other intangible losses.

To the fullest extent permitted by applicable law, Veacon is also not responsible for loss or harm caused by or arising from:
• the conduct of other users
• user-created or user-hosted activities, events, plans, or interactions
• independent event organizers or venues
• third-party websites, platforms, products, or services
• cancellations, schedule changes, refunds, or external transactions
• inaccurate, incomplete, or outdated information supplied by users or third parties
• interruptions, errors, or unavailable features of the Service

Nothing in these Terms limits or excludes liability where doing so would be prohibited by applicable law.`,
      },
      {
        q: "16) Intellectual-property complaints",
        short: "Send intellectual-property complaints to support@veacon.app.",
        a: `If you believe content available through Veacon infringes your copyright, trademark, or other intellectual-property rights, send a notice to support@veacon.app.

The notice should provide enough information to identify the content and understand the basis of the complaint.

Content may be removed or restricted where appropriate, and additional information may be requested before action is taken.`,
      },
      {
        q: "17) Changes to these Terms",
        short: "These Terms may be updated as Veacon evolves.",
        a: `These Terms may be updated from time to time to reflect changes to Veacon or applicable legal, operational, safety, or security requirements.

When these Terms are updated, the “Last updated” date will be revised.

If a change is material, notice of the updated Terms will be made available on the applicable Veacon domain and through the Veacon platform where appropriate or required by applicable law.

Continued use of Veacon after updated Terms become effective may constitute acceptance of the updated Terms to the extent permitted by applicable law.`,
      },
      {
        q: "18) Support and legal notices",
        short: "Questions or notices regarding these Terms can be sent to Veacon support.",
        a: `Questions, complaints, intellectual-property notices, or other communications concerning these Terms may be sent to:

support@veacon.app

Veacon
veacon.app`,
      },
    ],
    []
  );

  const [openIndex, setOpenIndex] = useState(0);

  return (
  <div className="min-h-screen bg-[rgb(var(--bg-app))] text-[rgb(var(--text-main))]">
    <div
      className="h-1.5 w-full"
      style={{
        background:
          "linear-gradient(90deg, rgb(var(--brand)), rgb(var(--cool)), rgb(var(--event)))",
      }}
    />

    <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[rgb(var(--text-main))] sm:text-4xl">
            Terms of Service
          </h1>

          <p className="mt-2 max-w-2xl text-sm leading-6 text-[rgb(var(--text-muted))] sm:text-base">
            These Terms govern your use of Veacon.
          </p>

          <p className="mt-1 text-xs font-medium text-[rgb(var(--text-muted))] sm:text-sm">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/privacy"
            className="
              rounded-xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              px-4 py-2.5
              text-sm font-bold
              text-[rgb(var(--text-main))]
              shadow-sm
              transition-colors
              hover:bg-[rgb(var(--elevated))]
            "
          >
            Privacy
          </Link>

          <Link
            to="/"
            className="
              rounded-xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              px-4 py-2.5
              text-sm font-bold
              text-[rgb(var(--text-main))]
              shadow-sm
              transition-colors
              hover:bg-[rgb(var(--elevated))]
            "
          >
            Home
          </Link>
        </div>
      </div>

      <section
        className="
          rounded-3xl
          border border-[rgb(var(--border))]
          bg-[rgb(var(--surface))]
          p-3
          shadow-sm
          sm:p-5
        "
      >
        <div className="grid gap-3">
          {items.map((item, idx) => (
            <AccordionItem
              key={item.q}
              item={item}
              isOpen={openIndex === idx}
              onToggle={() =>
                setOpenIndex((prev) => (prev === idx ? -1 : idx))
              }
            />
          ))}
        </div>
      </section>

      <p className="mt-6 text-center text-xs leading-6 text-[rgb(var(--text-muted))] sm:text-sm">
        Questions about these Terms?{" "}
        <a
          href="mailto:support@veacon.app"
          className="
            font-bold
            text-[rgb(var(--text-main))]
            underline
            decoration-[rgb(var(--border))]
            underline-offset-4
            transition-opacity
            hover:opacity-70
          "
        >
          support@veacon.app
        </a>
      </p>
    </main>
  </div>
);
}
